<script setup lang="ts">
import { computed, inject, useId } from 'vue'
import AdvancedSettings from './AdvancedSettings.vue'
import BinaryReferencePicker from './BinaryReferencePicker.vue'
import MemberNameField from './MemberNameField.vue'
import { referenceOf } from './binary-reference'
import { memberSuggestions, referencedNode } from './member-names'
import BbelField from '@/views/builder/components/bbel/BbelField.vue'
import type { BinaryReference, FilterServiceModel, JoinServiceModel } from '@/types'
import type { ScopeDescriptor } from '@/types/bbel-types'
import { BBEL_SCOPE_KEY, EMPTY_BBEL_SCOPE, type BbelScopeContext } from '@/views/builder/composables/bbel-context'

// Join and Filter look a row up in an array. They author the same four fields, and only the rows Include lets
// through differ, so one editor serves both.
type MatchServiceModel = JoinServiceModel | FilterServiceModel

const props = defineProps<{
  modelValue: MatchServiceModel
}>()

const emit = defineEmits<{
  'update:modelValue': [value: MatchServiceModel]
}>()

// Include is typed as text here because its allowed values depend on the kind. The select offers only the
// values of the kind being edited, so the cast holds.
function update(changes: Partial<Omit<JoinServiceModel, '$type' | 'include'>> & { include?: string }) {
  emit('update:modelValue', { ...props.modelValue, ...changes } as MatchServiceModel)
}

// In is a picker only. Publish refuses an expression there.
function updateIn(reference: BinaryReference) {
  update({ in: { $type: 'binary', reference } })
}

const includeOptions = computed(() =>
  props.modelValue.$type === 'join' ? ['Matched', 'Everything'] : ['Matched', 'Unmatched'],
)

const scopeCtx = inject<BbelScopeContext | undefined>(BBEL_SCOPE_KEY, undefined)
const scope = computed<ScopeDescriptor>(() => scopeCtx?.value ?? EMPTY_BBEL_SCOPE)

// In key names where the key sits inside one element of the array In points at.
const inNode = computed(() => referencedNode(scope.value.roots, referenceOf(props.modelValue.in)))
const inKeySuggestions = computed(() => memberSuggestions(inNode.value, 'chains'))

const includeId = useId()
</script>

<template>
  <div>
    <div class="mb-3">
      <div class="form-label">In</div>
      <BinaryReferencePicker label="In" :model-value="referenceOf(modelValue.in)" @update:model-value="updateIn" />
    </div>

    <div class="mb-3">
      <div class="form-label">In key</div>
      <MemberNameField
        label="In key"
        placeholder="Each element is the key"
        :model-value="modelValue.inKey"
        :suggestions="inKeySuggestions"
        @update:model-value="update({ inKey: $event })"
      />
      <div class="form-text">Two elements with the same key stop the run.</div>
    </div>

    <div class="mb-3">
      <div class="form-label">Key</div>
      <!-- Key is an expression: it is rendered against each row (MatchService.cs:21-25). -->
      <BbelField
        :model-value="modelValue.key"
        :scope="scope"
        single-line
        @update:model-value="update({ key: $event })"
      />
    </div>

    <div class="mb-3">
      <label class="form-label" :for="includeId">Include</label>
      <select
        :id="includeId"
        class="form-select w-auto"
        :value="modelValue.include"
        @change="update({ include: ($event.target as HTMLSelectElement).value })"
      >
        <option v-for="option in includeOptions" :key="option" :value="option">{{ option }}</option>
      </select>
    </div>

    <AdvancedSettings />
  </div>
</template>
